/**
 * AP-37 9. — **Kalkulationsliste** im Listen-Standard (FilterBar, Spalten,
 * Sortierung, Seiten, Export per Datei und E-Mail). Dieselbe Seite zeigt mit
 * `vorlagen` den Vorlagenkasten (4.17): ohne Kunde, ohne Status.
 *
 * Paginiert, gefiltert und sortiert auf dem Server. Muster: `ProjectsPage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Plus, Settings } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { useAuthStore } from '@/lib/auth.store'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { calculationsExportRequest, fetchCalculations, lookupUsers, type PaginatedCalculations } from '../api/calculations.api'
import { CalculationCreateDialog } from '../components/CalculationCreateDialog'
import { STATUS_ANZEIGE, abgabeDringend, datumText, kundeText, prozentText, stundenText, tagText } from '../lib/kalkulation-labels'
import { rememberListContext } from '../lib/kalkulation-navigation'
import type { CalcLookupUser, CalculationListRow, CalculationStatus, ListCalculationsParams } from '../types/calculation.types'

const STATUS_WERTE: CalculationStatus[] = ['IN_PROGRESS', 'SUBMITTED', 'WON', 'LOST', 'DISCARDED']

const buildFilterFields = (users: CalcLookupUser[], vorlagen: boolean): FilterFieldDef[] => [
  { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  ...(vorlagen
    ? []
    : ([
        {
          key: 'status',
          label: 'Status',
          type: 'select-multi',
          category: 'core',
          operators: ['is', 'is_not'],
          options: STATUS_WERTE.map((s) => ({ value: s, label: STATUS_ANZEIGE[s].label })),
        },
        { key: 'clientName', label: 'Kunde', type: 'text', category: 'core', operators: ['contains'] },
        { key: 'dueFrom', label: 'Abgabe ab', type: 'date', category: 'core', operators: ['is'] },
        { key: 'dueTo', label: 'Abgabe bis', type: 'date', category: 'core', operators: ['is'] },
        { key: 'submittedFrom', label: 'abgegeben von', type: 'date', category: 'core', operators: ['is'] },
        { key: 'submittedTo', label: 'abgegeben bis', type: 'date', category: 'core', operators: ['is'] },
        // AP-40 7.2: Trefferquote — gewonnen oder verloren ab diesem Tag.
        { key: 'decidedFrom', label: 'entschieden ab', type: 'date', category: 'core', operators: ['is'] },
        { key: 'allRevisions', label: 'alle Fassungen', type: 'boolean', category: 'core', operators: ['is'] },
      ] as FilterFieldDef[])),
  {
    key: 'owner',
    label: 'Kalkulant',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: users.map((u) => ({ value: u.id, label: u.name })),
  },
]

interface ColDef {
  key: string
  label: string
  align?: 'right'
  sortable?: boolean
  /** nur mit `calculation.cost.view` (Feldrecht, AP-37 7.) */
  kosten?: boolean
  /** nicht im Vorlagenkasten */
  ohneVorlage?: boolean
}

const ALLE_SPALTEN: ColDef[] = [
  { key: 'number', label: 'Nummer', sortable: true },
  { key: 'name', label: 'Bezeichnung', sortable: true },
  { key: 'client', label: 'Kunde', sortable: true, ohneVorlage: true },
  { key: 'owner', label: 'Kalkulant', sortable: true },
  { key: 'dueAt', label: 'Abgabe', sortable: true, ohneVorlage: true },
  { key: 'status', label: 'Status', sortable: true, ohneVorlage: true },
  // Detailblatt K 7.3 (K-22): letzte Abgabe der Fassung — ab Werk sichtbar.
  { key: 'submittedAt', label: 'abgegeben am', sortable: true, ohneVorlage: true },
  { key: 'submittedVia', label: 'abgegeben als', ohneVorlage: true },
  { key: 'priceAfterDiscount', label: 'Angebotssumme', align: 'right', sortable: true },
  { key: 'contributionMarginPercent', label: 'DB %', align: 'right', kosten: true },
  { key: 'hoursTotal', label: 'Stunden', align: 'right', sortable: true },
  { key: 'pricedPercent', label: 'ausgepreist', align: 'right' },
  // Detailblatt O 10.: offen · angefragt · Angebot da — ab Werk aus.
  { key: 'material', label: 'Material', ohneVorlage: true },
  { key: 'validUntil', label: 'Bindefrist', ohneVorlage: true },
  { key: 'createdAt', label: 'angelegt am', sortable: true },
]

const DEFAULT_COLUMN_KEYS = ['name', 'client', 'owner', 'dueAt', 'status', 'submittedAt', 'submittedVia', 'priceAfterDiscount', 'contributionMarginPercent', 'hoursTotal', 'pricedPercent']
const DEFAULT_FILTER_FIELD_KEYS = ['search', 'status', 'owner']
/** Vorgabe „aktuell offen" (Designvorschlag): in Arbeit und abgegeben. */
const DEFAULT_FILTER_VALUES: Record<string, ActiveFilter> = {
  status: { fieldKey: 'status', operator: 'is', value: 'IN_PROGRESS,SUBMITTED' },
}

function filterParams(filters: Record<string, ActiveFilter>): ListCalculationsParams {
  const p: ListCalculationsParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'search') p.search = a.value
    if (key === 'status') {
      if (a.operator === 'is_not') p.statusNot = a.value
      else p.status = a.value
    }
    if (key === 'clientName') p.clientName = a.value
    if (key === 'owner') p.ownerUserId = a.value
    if (key === 'dueFrom') p.dueFrom = a.value.slice(0, 10)
    if (key === 'dueTo') p.dueTo = a.value.slice(0, 10)
    if (key === 'submittedFrom') p.submittedFrom = a.value.slice(0, 10)
    if (key === 'submittedTo') p.submittedTo = a.value.slice(0, 10)
    if (key === 'decidedFrom') p.decidedFrom = a.value.slice(0, 10)
    if (key === 'allRevisions') p.allRevisions = a.value === 'true'
  }
  return p
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

function StatusPill({ status }: { status: CalculationStatus }) {
  const s = STATUS_ANZEIGE[status]
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.klasse}`}>{s.label}</span>
}

export default function CalculationsPage({ vorlagen = false }: { vorlagen?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const darfAnlegen = erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const layoutKey = vorlagen ? 'columnbar_layout_calculation_templates' : 'columnbar_layout_calculations'

  const alleSpalten = useMemo(() => ALLE_SPALTEN.filter((c) => (!c.kosten || kosten) && (!vorlagen || !c.ohneVorlage)), [kosten, vorlagen])

  const [users, setUsers] = useState<CalcLookupUser[]>([])
  useEffect(() => {
    lookupUsers().then(setUsers).catch(() => setUsers([]))
  }, [])
  const filterFields = useMemo(() => buildFilterFields(users, vorlagen), [users, vorlagen])
  // AP-40 6.4: Filter aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, buildFilterFields([], vorlagen)))

  const ladeSpalten = (): ColumnState => {
    const gespeichert = loadUiSetting<Partial<ColumnState> | null>(layoutKey, null)
    if (gespeichert?.columnKeys?.length) {
      return {
        columnKeys: gespeichert.columnKeys,
        columnWidths: gespeichert.columnWidths ?? {},
        sortKey: gespeichert.sortKey ?? 'number',
        sortDir: gespeichert.sortDir ?? 'desc',
      }
    }
    return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'number', sortDir: 'desc' }
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<PaginatedCalculations | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [anlegen, setAnlegen] = useState(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const params: ListCalculationsParams = {
          ...filterParams(filters),
          templates: vorlagen,
          page: seite,
          limit: 50,
          sortBy: state.sortKey,
          sortDir: state.sortDir,
        }
        setResult(await fetchCalculations(params))
        rememberListContext(user?.id, { ...filterParams(filters), templates: vorlagen, sortBy: state.sortKey, sortDir: state.sortDir })
      } catch {
        setError(vorlagen ? 'Die Vorlagen ließen sich nicht laden.' : 'Die Kalkulationen ließen sich nicht laden.')
      } finally {
        setLoading(false)
      }
    },
    [vorlagen, user?.id],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, vorlagen])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback(
    (updater: (prev: ColumnState) => ColumnState) => {
      setColState((prev) => {
        const next = updater(prev)
        saveUiSetting(layoutKey, next)
        return next
      })
    },
    [layoutKey],
  )

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(60, startW + ev.clientX - startX)
      updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: w } }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys
    .filter((k) => k !== 'number')
    .map((k) => alleSpalten.find((c) => c.key === k))
    .filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => c.key !== 'number' && !colState.columnKeys.includes(c.key))

  const renderCell = (z: CalculationListRow, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'name':
        return z.name
      case 'client':
        return kundeText(z) || '—'
      case 'owner':
        return z.ownerName ?? '—'
      case 'dueAt':
        return z.dueAt ? (
          <span className={abgabeDringend(z.dueAt) && z.status === 'IN_PROGRESS' ? 'font-semibold text-destructive' : undefined}>
            {datumText(z.dueAt, true)}
          </span>
        ) : (
          '—'
        )
      case 'status':
        return <StatusPill status={z.status} />
      case 'priceAfterDiscount':
        return geldText(z.priceAfterDiscount) || '—'
      case 'contributionMarginPercent':
        return prozentText(z.contributionMarginPercent) || '—'
      case 'hoursTotal':
        return stundenText(z.hoursTotal)
      case 'pricedPercent':
        return z.pricedPercent == null ? '—' : `${z.pricedPercent} %`
      case 'validUntil':
        return tagText(z.validUntil) || '—'
      case 'submittedAt':
        return z.submittedAt ? datumText(z.submittedAt, true) : ''
      case 'submittedVia':
        return z.submittedVia ?? ''
      case 'material':
        return z.material && (z.material.open || z.material.requested || z.material.answered) ? (
          <span className="text-xs tabular-nums" title="offen · angefragt · Angebot da">
            {z.material.open} · {z.material.requested} · <span className={z.material.answered ? 'font-semibold text-emerald-700 dark:text-emerald-300' : undefined}>{z.material.answered}</span>
          </span>
        ) : (
          ''
        )
      case 'createdAt':
        return datumText(z.createdAt)
      default:
        return null
    }
  }

  const rows = result?.data ?? []
  const meta = result?.meta
  const summe = rows.reduce((s, z) => s + (z.priceAfterDiscount ?? 0), 0)
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const titel = vorlagen ? 'Vorlagen' : 'Kalkulationen'
  const pfeil = (key: string) =>
    colState.sortKey === key ? (
      colState.sortDir === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-primary" />
      ) : (
        <ArrowDown className="h-3 w-3 text-primary" />
      )
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30" />
    )

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{titel}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {vorlagen
              ? 'Vorlagenkasten: Positionen mit Material und Arbeitsschritten je Einheit — kopieren und in eine Kalkulation einfügen.'
              : 'Nummer, Kunde, Abgabe, Status und Angebotssumme — ein Klick öffnet die Kalkulation.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            request={(format, orientation) =>
              calculationsExportRequest(
                { ...filterParams(filterValues), templates: vorlagen, sortBy: colState.sortKey, sortDir: colState.sortDir, format, orientation },
                [{ key: 'label', label: 'Nummer' }, ...shownColumns.map((c) => ({ key: c.key === 'owner' ? 'owner' : c.key, label: c.label }))],
              )
            }
            mail={{ source: 'EXPORT_CALCULATIONS', title: t('mailExport.titles.EXPORT_CALCULATIONS'), subject: titel }}
          />
          {darfAnlegen && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground" onClick={() => setAnlegen(true)}>
              <Plus className="h-4 w-4" /> {vorlagen ? 'Neue Vorlage' : 'Neue Kalkulation'}
            </button>
          )}
        </div>
      </div>

      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey={vorlagen ? 'calculation_templates' : 'calculations'}
        defaultFieldKeys={vorlagen ? ['search', 'owner'] : DEFAULT_FILTER_FIELD_KEYS}
        defaultValues={vorlagen ? {} : DEFAULT_FILTER_VALUES}
        adresse={adresse}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={layoutKey}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'number', sortDir: 'desc' as const }
                markUiSettingApplied(layoutKey, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="relative px-4 py-1.5 text-left font-medium text-foreground" style={colState.columnWidths.number ? { width: colState.columnWidths.number } : undefined}>
                  <button type="button" onClick={() => handleSort('number')} className="flex items-center gap-1 hover:text-primary">
                    Nummer {pfeil('number')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, 'number')} />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      columnConfigMode ? 'cursor-grab' : ''
                    } ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {vorlagen ? 'Noch keine Vorlagen — „Als Vorlage" im Menü einer Kalkulation legt eine an.' : 'Keine Kalkulationen gefunden.'}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr
                  key={z.id}
                  onClick={() => navigate(`/calculations/${z.id}`)}
                  className={`cursor-pointer border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}
                >
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                    {z.number}
                    {z.revision > 1 && <span className="text-muted-foreground"> · F{z.revision}</span>}
                    {!z.isCurrent && <span className="ml-1 text-xs text-muted-foreground">(alt)</span>}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`whitespace-nowrap px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                    {meta ? `${meta.total} ${titel}` : `${rows.length} ${titel}`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {col.key === 'priceAfterDiscount' ? geldText(summe) : ''}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              Seite {meta.page} von {meta.totalPages} ({meta.total} Einträge)
            </span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {anlegen && (
        <CalculationCreateDialog
          vorlage={vorlagen}
          onClose={() => setAnlegen(false)}
          onSaved={(k) => {
            setAnlegen(false)
            navigate(`/calculations/${k.id}`)
          }}
        />
      )}
    </div>
  )
}
